import { invalidateMatterAndSearchQueries } from "@/lib/data/react-query/search/invalidation";
import {
  DateFormField,
  Form,
  SimpleSelectFormField,
  Text,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AddOrEditFileReferenceSlideover } from "@/components/features/matter/file-repo/AddOrEditFileReferenceSlideover";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { useUser } from "@/components/UserProvider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/shadcn/Popover/Popover";
import type { TreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { useCreateMatterFileReference } from "@/lib/data/react-query/matters/matter-file";
import {
  useCreateMedicalTreatmentAppointmentConnection,
  useUpdateMedicalTreatmentAppointment,
} from "@/lib/data/react-query/matters/medical-treatment-appointment";
import {
  useCreateMedicalTreatmentBill,
  useUpdateMedicalTreatmentBill,
} from "@/lib/data/react-query/matters/medical-treatment-bill";
import {
  useCreateMedicalTreatmentRecord,
  useUpdateMedicalTreatmentRecord,
} from "@/lib/data/react-query/matters/medical-treatment-record";
import { MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterFile } from "@/lib/types";
import { trimText } from "@/lib/utils/format";
import { mergeFilesAndReferenceId } from "@/lib/utils/matterFile";
import { MODAL_EXIT_ANIMATION_MS } from "@/lib/utils/modalSideEffectCleanup";
import {
  getItemDateModified,
  getItemReceivedDate,
  getItemRejectedDate,
  getItemRequestDate,
  getItemStatus,
} from "@/components/features/matter/medical-treatments/appointmentStatusEditorUtils";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, FileText, Plus } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

type StatusOption =
  (typeof MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)[number];
type AppointmentBill =
  TreatmentViewModel["appointments"][number]["bills"][number];
type AppointmentRecord =
  TreatmentViewModel["appointments"][number]["records"][number];
type StatusEditorFormValues = {
  status: StatusOption | "";
  requestDate: string;
  receivedDate: string;
  name: string;
  rejectedDate: string;
  rejectedReason: string;
};

const suppressFocusRingClass =
  "focus:ring-0 focus:ring-offset-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-within:ring-0 focus-within:ring-offset-0";

export function AppointmentStatusEditorPopover({
  children,
  appointmentId,
  matter,
  items,
  treatmentId,
  type,
}: {
  children: ReactNode;
  appointmentId: string;
  matter: Matter;
  items: (AppointmentBill | AppointmentRecord)[];
  treatmentId: string;
  type: "bill" | "record";
}) {
  const { hasActionPermission } = useUser();
  const [open, setOpen] = useState(false);
  const [isFileRepositoryOpen, setIsFileRepositoryOpen] = useState(false);
  const [isFileRepositoryLayerActive, setIsFileRepositoryLayerActive] =
    useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [attachedFiles, setAttachedFiles] = useState<MatterFile[]>([]);
  const isStatusSelectOpenRef = useRef(false);
  const isFileRepositoryOpenRef = useRef(false);
  const fileRepositoryCloseTimeoutRef = useRef<number | null>(null);
  const initializedItemKeyRef = useRef<string | null>(null);
  const form = useForm<StatusEditorFormValues>({
    defaultValues: {
      status: "",
      requestDate: "",
      receivedDate: "",
      name: "",
      rejectedDate: "",
      rejectedReason: "",
    },
  });
  const queryClient = useQueryClient();
  const status = form.watch("status");
  const createAppointmentConnection =
    useCreateMedicalTreatmentAppointmentConnection({
      suppressQueryInvalidation: true,
    });
  const updateAppointment = useUpdateMedicalTreatmentAppointment({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const createBill = useCreateMedicalTreatmentBill({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const createRecord = useCreateMedicalTreatmentRecord({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updateBill = useUpdateMedicalTreatmentBill({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updateRecord = useUpdateMedicalTreatmentRecord({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const createMatterFileReference = useCreateMatterFileReference();
  const appointment = matter.medicalTreatmentAppointments.find(
    (currentAppointment) => currentAppointment.id === appointmentId,
  );
  const item = items.at(activeItemIndex);
  const hasMultipleItems = items.length > 1;
  const hasNoItems = items.length === 0;
  const itemPermissionKey =
    type === "bill"
      ? ACTION_PERMISSION_KEYS.matterMedicalTreatmentBill
      : ACTION_PERMISSION_KEYS.matterMedicalTreatmentRecord;
  const canSaveItem = item
    ? hasActionPermission(itemPermissionKey, ACTION_PERMISSION_VALUES.update)
    : hasActionPermission(itemPermissionKey, ACTION_PERMISSION_VALUES.create) &&
      hasActionPermission(
        ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
        ACTION_PERMISSION_VALUES.create,
      );
  const canUpdateAppointment = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointment,
    ACTION_PERMISSION_VALUES.update,
  );
  const initializedItemKey = `${type}:${activeItemIndex}:${
    item
      ? type === "bill"
        ? (item as AppointmentBill).id
        : (item as AppointmentRecord).id
      : "new"
  }`;

  useEffect(() => {
    if (!open) {
      initializedItemKeyRef.current = null;
      return;
    }

    if (initializedItemKeyRef.current === initializedItemKey) {
      return;
    }

    initializedItemKeyRef.current = initializedItemKey;

    if (!item) {
      form.reset({
        status: "",
        requestDate: "",
        receivedDate: "",
        name: "",
        rejectedDate: "",
        rejectedReason: "",
      });
      setAttachedFiles([]);
      return;
    }

    const itemStatus = getItemStatus(item);
    const formStatus =
      itemStatus &&
      MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS.includes(
        itemStatus as StatusOption,
      )
        ? (itemStatus as StatusOption)
        : "";
    const today = dayjs().format("YYYY-MM-DD");
    form.reset({
      status: formStatus,
      requestDate:
        getItemRequestDate(item) ||
        (["Requested", "Received"].includes(formStatus) ? today : ""),
      receivedDate:
        getItemReceivedDate(item) || (formStatus === "Received" ? today : ""),
      name:
        type === "bill"
          ? trimText((item as AppointmentBill).recordLabel)
          : trimText((item as AppointmentRecord).name),
      rejectedDate:
        getItemRejectedDate(item) || (formStatus === "Rejected" ? today : ""),
      rejectedReason:
        "recordRejectedReason" in item
          ? (item.recordRejectedReason ?? "")
          : (("rejectedReason" in item ? item.rejectedReason : "") ?? ""),
    });
    setAttachedFiles([]);
  }, [form, initializedItemKey, item, open, type]);

  useEffect(() => {
    if (open) setActiveItemIndex(0);
  }, [open]);

  useEffect(
    () => () => {
      if (fileRepositoryCloseTimeoutRef.current != null) {
        window.clearTimeout(fileRepositoryCloseTimeoutRef.current);
      }
    },
    [],
  );

  const itemId = item
    ? type === "bill"
      ? (item as AppointmentBill).id
      : (item as AppointmentRecord).id
    : `new-${type}`;
  const itemLabel = item
    ? type === "bill"
      ? trimText((item as AppointmentBill).recordLabel) || "-"
      : trimText((item as AppointmentRecord).name) || "-"
    : `New ${type}`;
  const existingFiles = mergeFilesAndReferenceId(
    matter,
    type === "bill" ? "medicalTreatmentBillId" : "medicalTreatmentRecordId",
    itemId,
  );
  const showRequestDate = ["Requested", "Received"].includes(status);
  const showReceivedFields = status === "Received";
  const showRejectedDate = status === "Rejected";
  const isPending =
    createAppointmentConnection.isPending ||
    updateAppointment.isPending ||
    createBill.isPending ||
    createRecord.isPending ||
    updateBill.isPending ||
    updateRecord.isPending ||
    createMatterFileReference.isPending;
  const handleActiveItemIndexChange = (nextIndex: number) => {
    if (nextIndex === activeItemIndex) {
      return;
    }

    setActiveItemIndex(nextIndex);
  };
  const handleStatusChange = (newStatus: StatusOption) => {
    const today = dayjs().format("YYYY-MM-DD");

    if (
      ["Requested", "Received"].includes(newStatus) &&
      !form.getValues("requestDate")
    ) {
      form.setValue("requestDate", today, { shouldDirty: true });
    }

    if (newStatus === "Received" && !form.getValues("receivedDate")) {
      form.setValue("receivedDate", today, { shouldDirty: true });
    }

    if (newStatus === "Rejected" && !form.getValues("rejectedDate")) {
      form.setValue("rejectedDate", today, { shouldDirty: true });
    }
  };

  const handleFileRepositoryOpenChange = (nextOpen: boolean) => {
    if (fileRepositoryCloseTimeoutRef.current != null) {
      window.clearTimeout(fileRepositoryCloseTimeoutRef.current);
      fileRepositoryCloseTimeoutRef.current = null;
    }

    if (nextOpen) {
      isFileRepositoryOpenRef.current = true;
      flushSync(() => {
        setIsFileRepositoryLayerActive(true);
      });
      setIsFileRepositoryOpen(true);
      return;
    }

    setIsFileRepositoryOpen(false);
    fileRepositoryCloseTimeoutRef.current = window.setTimeout(() => {
      isFileRepositoryOpenRef.current = false;
      setIsFileRepositoryLayerActive(false);
      fileRepositoryCloseTimeoutRef.current = null;
    }, MODAL_EXIT_ANIMATION_MS);
  };

  const handleMarkNotApplicable = async () => {
    if (!canUpdateAppointment) return;
    if (!appointment) {
      toast.error("Unable to find treatment date");
      return;
    }

    await updateAppointment.mutateAsync({
      matterId: matter.id,
      id: appointmentId,
      dateModified: appointment.dateModified,
      medicalTreatmentId: appointment.medicalTreatmentId,
      date: appointment.date,
      type: appointment.type,
      ...(type === "bill"
        ? { billNotApplicable: true }
        : { recordNotApplicable: true }),
    });

    toast.success(
      `${type === "bill" ? "Bills" : "Records"} marked not applicable`,
    );
    setOpen(false);
    void invalidateMatterAndSearchQueries(queryClient);
  };

  const handleSave = async () => {
    if (!canSaveItem) return;
    const values = form.getValues();
    const selectedStatus = values.status;
    if (!selectedStatus) {
      toast.error(`Select a ${type} status before saving`);
      return;
    }

    const today = dayjs().format("YYYY-MM-DD");
    const requestDate = values.requestDate || today;
    const receivedDate = values.receivedDate || today;
    const name = values.name.trim();
    const rejectedDate = values.rejectedDate || today;
    const rejectedReason = values.rejectedReason.trim();
    const shouldSaveRequestDate = ["Requested", "Received"].includes(
      selectedStatus,
    );
    const shouldSaveReceivedFields = selectedStatus === "Received";
    const shouldSaveRejectedDate = selectedStatus === "Rejected";

    if (shouldSaveRejectedDate && !rejectedReason) {
      toast.error("Enter a rejected reason before saving");
      return;
    }

    const billDatePayload = {
      ...(shouldSaveRequestDate ? { recordRequestDate: requestDate } : {}),
      ...(shouldSaveReceivedFields ? { recordReceivedDate: receivedDate } : {}),
      ...(shouldSaveRejectedDate ? { recordRejectedDate: rejectedDate } : {}),
      ...(shouldSaveRejectedDate
        ? { recordRejectedReason: rejectedReason }
        : {}),
      ...(shouldSaveReceivedFields ? { recordLabel: name } : {}),
    };
    const recordDatePayload = {
      ...(shouldSaveRequestDate ? { requestDate: requestDate } : {}),
      ...(shouldSaveReceivedFields ? { receivedDate: receivedDate } : {}),
      ...(shouldSaveRejectedDate ? { rejectedDate: rejectedDate } : {}),
      ...(shouldSaveRejectedDate ? { rejectedReason } : {}),
      ...(shouldSaveReceivedFields ? { name } : {}),
    };
    let savedItemId = itemId;

    if (!item && type === "bill") {
      const result = await createBill.mutateAsync({
        matterId: matter.id,
        medicalTreatmentId: treatmentId,
        recordStatus: selectedStatus,
        originalBill: null,
        ...billDatePayload,
      });
      savedItemId = result.data.id;
      await createAppointmentConnection.mutateAsync({
        matterId: matter.id,
        medicalTreatmentAppointmentId: appointmentId,
        medicalTreatmentBillId: savedItemId,
      });
    } else if (!item) {
      const result = await createRecord.mutateAsync({
        matterId: matter.id,
        medicalTreatmentId: treatmentId,
        status: selectedStatus,
        ...recordDatePayload,
      });
      savedItemId = result.data.id;
      await createAppointmentConnection.mutateAsync({
        matterId: matter.id,
        medicalTreatmentAppointmentId: appointmentId,
        medicalTreatmentRecordId: savedItemId,
      });
    } else if (type === "bill") {
      await updateBill.mutateAsync({
        matterId: matter.id,
        dateModified: getItemDateModified(item),
        id: itemId,
        recordStatus: selectedStatus,
        ...billDatePayload,
      });
    } else {
      await updateRecord.mutateAsync({
        matterId: matter.id,
        dateModified: getItemDateModified(item),
        id: itemId,
        status: selectedStatus,
        ...recordDatePayload,
      });
    }

    if (shouldSaveReceivedFields && attachedFiles.length > 0) {
      await Promise.all(
        attachedFiles.map((file) =>
          createMatterFileReference.mutateAsync({
            matterId: matter.id,
            fileId: file.id,
            medicalTreatmentBillId: type === "bill" ? savedItemId : undefined,
            medicalTreatmentRecordId:
              type === "record" ? savedItemId : undefined,
          }),
        ),
      );
    }

    toast.success(
      `${type === "bill" ? "Bill" : "Record"} status updated successfully`,
    );
    setOpen(false);
    void invalidateMatterAndSearchQueries(queryClient);
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isFileRepositoryOpenRef.current) {
            return;
          }
          setOpen(nextOpen);
        }}
      >
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          className="w-[330px] space-y-4 rounded-lg p-4 shadow-lg"
          style={{
            display: isFileRepositoryLayerActive ? "none" : undefined,
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onInteractOutside={(event) => {
            if (
              isStatusSelectOpenRef.current ||
              isFileRepositoryOpenRef.current
            ) {
              event.preventDefault();
            }
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <Text variant="body" className="font-semibold">
              {type === "bill" ? "Bill status" : "Record status"}
            </Text>
            {hasMultipleItems && (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={isPending || activeItemIndex === 0}
                  aria-label={`Previous ${type}`}
                  onClick={() => {
                    handleActiveItemIndexChange(
                      Math.max(activeItemIndex - 1, 0),
                    );
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-muted-foreground min-w-10 text-center text-xs font-medium">
                  {activeItemIndex + 1} / {items.length}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={isPending || activeItemIndex === items.length - 1}
                  aria-label={`Next ${type}`}
                  onClick={() => {
                    handleActiveItemIndexChange(
                      Math.min(activeItemIndex + 1, items.length - 1),
                    );
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          {!hasNoItems && (
            <div className="rounded-md border px-3 py-2">
              <Text variant="body" className="font-medium">
                {itemLabel}
              </Text>
            </div>
          )}
          <Form {...form}>
            <div key={itemId} className="animate-in fade-in-0 duration-150">
              <fieldset className="min-w-0 space-y-4" disabled={isPending}>
                <SimpleSelectFormField
                  register={form.register("status")}
                  formatItemLabel={(option) => option}
                  formItemClassName="space-y-2"
                  className={suppressFocusRingClass}
                  items={MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS}
                  placeholder="Select status"
                  disabled={isPending}
                  onChange={(value) => {
                    if (!value) return;
                    handleStatusChange(value as StatusOption);
                  }}
                  onOpenChange={(nextOpen) => {
                    if (nextOpen) {
                      isStatusSelectOpenRef.current = true;
                      return;
                    }

                    setTimeout(() => {
                      isStatusSelectOpenRef.current = false;
                    }, 0);
                  }}
                />
                {showRequestDate && (
                  <DateFormField
                    name="requestDate"
                    register={form.register("requestDate")}
                    label="Requested date*"
                    groupClassName={suppressFocusRingClass}
                    fieldClassName={suppressFocusRingClass}
                  />
                )}
                {showReceivedFields && (
                  <>
                    <DateFormField
                      name="receivedDate"
                      register={form.register("receivedDate")}
                      label="Received date*"
                      groupClassName={suppressFocusRingClass}
                      fieldClassName={suppressFocusRingClass}
                    />
                    <TextFormField
                      label={`${type === "bill" ? "Bill" : "Record"} name`}
                      placeholder={`Enter ${type} name`}
                      register={form.register("name")}
                      maxLength={50}
                    />
                    <div className="space-y-2">
                      <label className="text-muted-foreground text-sm font-medium">
                        Attached file
                      </label>
                      {existingFiles.length > 0 && (
                        <div className="space-y-1">
                          {existingFiles.map((file: MatterFile) => (
                            <div
                              key={file.id}
                              className="border-border flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm"
                            >
                              <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                              <span className="min-w-0 flex-1 truncate">
                                {file.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {attachedFiles.length > 0 && (
                        <div className="space-y-1">
                          {attachedFiles.map((file) => (
                            <div
                              key={file.id}
                              className="border-border flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm"
                            >
                              <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                              <span className="min-w-0 flex-1 truncate">
                                {file.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        className="text-muted-foreground hover:bg-muted/50 h-auto w-full min-w-0 justify-center gap-2 overflow-hidden border-dashed px-3 py-2 font-normal"
                        disabled={isPending}
                        onClick={() => {
                          handleFileRepositoryOpenChange(true);
                        }}
                      >
                        <Plus className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 truncate">
                          Select or upload file
                        </span>
                      </Button>
                    </div>
                  </>
                )}
                {showRejectedDate && (
                  <>
                    <DateFormField
                      name="rejectedDate"
                      register={form.register("rejectedDate")}
                      label="Rejected date*"
                      groupClassName={suppressFocusRingClass}
                      fieldClassName={suppressFocusRingClass}
                    />
                    <TextAreaFormField
                      register={form.register("rejectedReason")}
                      label="Rejected reason*"
                    />
                  </>
                )}
              </fieldset>
            </div>
          </Form>
          {hasNoItems && status !== "Received" && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="bg-border h-px flex-1" />
                <Text
                  variant="caption"
                  color="muted"
                  as="span"
                  className="font-semibold tracking-normal"
                >
                  OR
                </Text>
                <div className="bg-border h-px flex-1" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleMarkNotApplicable}
                disabled={isPending || !canUpdateAppointment}
              >
                Mark {type === "bill" ? "bills" : "records"} not applicable
              </Button>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isPending || !canSaveItem}
            >
              {isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <div
        className="contents"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <AddOrEditFileReferenceSlideover
          open={isFileRepositoryOpen}
          setOpen={handleFileRepositoryOpenChange}
          matter={matter}
          handleCreateMatterFileReference={(selectedFiles) => {
            setAttachedFiles(selectedFiles);
            handleFileRepositoryOpenChange(false);
          }}
          isCreating={false}
        />
      </div>
    </>
  );
}
